import { FormulaEditor } from "@app/objects/FormulaEditor";
import { PeriodEditor } from "@app/grid/PeriodEditor";
import { Button } from "@comps/ui/button";
import { Label } from "@comps/ui/label";
import { Popover, PopoverContent, PopoverTrigger } from "@comps/ui/popover";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@comps/ui/select";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@comps/ui/tooltip";
import Field, { AggregationType } from "@dto/fields";
import { Period, PeriodType, PeriodTypeMonth, PeriodTypeQuarter, PeriodTypeValues, PeriodTypeWeek, PeriodTypeYear, getPeriodTypeLabel } from "@dto/period";
import { AND, Filter, IN, INF_EQUAL, OR, SUP_EQUAL } from "@dto/records";
import { ReactComponent as FunctionSVG } from "@img/function.svg";
import { ReactComponent as FunctionStruckSVG } from "@img/function_struck.svg";
import { checkFormula, getFieldKey } from "@utils/model";
import { TW_AdvancedPeriodEditorContainer, TW_AdvancedPeriodEditorDateFormulaItem, TW_AdvancedPeriodEditorDateFormulasContainer, TW_AdvancedPeriodEditorFormulaToggle, TW_AdvancedPeriodEditorFormulaTogglePressed, TW_AdvancedPeriodEditorPeriodFormulaButtonValidate, TW_AdvancedPeriodEditorPeriodFormulaContainer, TW_AdvancedPeriodEditorSectionContainer, TW_AdvancedPeriodEditorSectionHeader, TW_AdvancedPeriodEditorSectionIcon, TW_AdvancedPeriodEditorSectionLabel, TW_AdvancedPeriodEditorShortcutButton, TW_AdvancedPeriodEditorShortcutButtonsContainer, TW_AdvancedPeriodEditorTrashIcon, TW_DateFilterValueEditor, TW_DateFilterValueEditor_DateFormulaButton, TW_DateFilterValueEditor_FunctionButton, TW_DateFilterValueEditor_PeriodFormulaButton, TW_DateFilterValueEditor_Wrapper, TW_DateFilterValueEditor_editing, TW_FilterEditorValue_Wrapper, TW_PeriodFilterValueEditor_From, TW_PeriodFilterValueEditor_To, TW_PeriodTypeSelect, TW_PeriodTypeSelectItem, TW_PeriodTypeSelect_selected, useIconHover } from "@utils/twClasses";
import { GRAY_300_COLOR, GRAY_600_COLOR, GRAY_800_COLOR } from "@utils/colors";
import { displayDate } from "@utils/types";
import { TFunction } from "i18next";
import { ChevronDown, ChevronRight } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { translate } from "src/i18n/i18n";
import { useModelStore, useUserStore } from "src/store";
import { currentPeriodOf, displayedPeriodType, fieldPeriodTypeOf, isFilterPeriodTypeAllowed, matchablePeriodType } from "./periodFilterLogic";

const AUTHORIZED_KEYS = ["Backspace", "Delete", "ArrowLeft", "ArrowRight", "-", "Home", "End"];

type PeriodFilterValueEditorProps = {
    field: Field | null;
    // Aggregation of the filtered column: a DATE (or PERIOD) column grouped by
    // month/quarter/... is filtered with the period editor of that granularity.
    aggregation?: AggregationType;
    selectedPathKey: string | undefined;
    filter: Filter;
    validateFilter: (filter: Filter) => void;
    disabled: boolean
}

const getPeriodShortcuts = (t: TFunction) => [
    { label: t("field.formula.variable.label.this_week"), formula: "${THIS_WEEK}", periodType: PeriodTypeWeek },
    { label: t("field.formula.variable.label.this_month"), formula: "${THIS_MONTH}", periodType: PeriodTypeMonth },
    { label: t("field.formula.variable.label.this_quarter"), formula: "${THIS_QUARTER}", periodType: PeriodTypeQuarter },
    { label: t("field.formula.variable.label.this_year"), formula: "${THIS_YEAR}", periodType: PeriodTypeYear },
]

export function PeriodFilterValueEditor({ field, aggregation, selectedPathKey, filter, validateFilter, disabled }: PeriodFilterValueEditorProps) {

    const fieldByKeys = useModelStore((state) => state.fieldByKeys);
    // Granularity of the stored values (undefined: not a period-like field),
    // of the displayed periods (the selector starts with it and refuses the
    // finer types) and of the finest matchable filter (gates the shortcuts of
    // the advanced editor). All may be 0 (Day): only compare them to undefined.
    const fieldPeriodType = fieldPeriodTypeOf(field, fieldByKeys);
    const defaultPeriodType = displayedPeriodType(field, aggregation, fieldByKeys);
    const [period, setPeriod] = useState<Period | undefined>(getFilterPeriod(filter));
    const [periodType, setPeriodType] = useState<PeriodType | undefined>(getFilterPeriod(filter)?.type ?? defaultPeriodType);
    const [periodFormula, setPeriodFormula] = useState<string | null>(getFilterFormula(filter));
    const [startDate, setStartDate] = useState<string | null>(null);
    const [startDateFormula, setStartDateFormula] = useState<string | null>(null);
    const [endDate, setEndDate] = useState<string | null>(null);
    const [endDateFormula, setEndDateFormula] = useState<string | null>(null);
    const [isFormulaOpen, setIsFormulaOpen] = useState(false);

    const { t } = useTranslation();
    const shortcuts = getPeriodShortcuts(t);

    const fieldKey = selectedPathKey ? getFieldKey(selectedPathKey) : undefined;
    const selectedWorkspace = useModelStore((state) => state.selectedWorkspace);

    function getFilterFormula(filter: Filter | undefined): string | null {
        if (!filter) return null;
        const filters = filter.groupFilter?.filters;
        if (filters) {
            for (const f of filters) {
                const sFilter = f.singleFilter;
                if (sFilter?.operator === IN && sFilter.formula) {
                    return sFilter.formula;
                }
            }
        }
        return null;
    }

    function getFilterPeriod(filter: Filter | undefined): Period | undefined {
        if (!filter) {
            return undefined;
        }
        const fieldFilter = filter.groupFilter;
        const filters = fieldFilter?.filters;
        if (filters) {
            let period: Period | undefined = undefined;
            filters.forEach(f => {
                if (f.singleFilter) {
                    const sFilter = f.singleFilter;
                    if (sFilter.operator == IN && sFilter.value) {
                        period = Period.fromObject(sFilter.value);
                    }
                }
            });
            return period;
        }
        return undefined;
    }

    useEffect(() => {
        if (filter) {
            const fieldFilter = filter.groupFilter;
            const filters = fieldFilter?.filters;
            if (filters) {
                filters.forEach(f => {
                    if (f.singleFilter) {
                        const sFilter = f.singleFilter;
                        if (sFilter.operator == IN && sFilter.formula) {
                            setPeriodFormula(sFilter.formula);
                            setPeriod(undefined);
                        } else if (sFilter.operator == IN && sFilter.value) {
                            const period = Period.fromObject(sFilter.value);
                            setPeriod(period);
                            setPeriodType(period?.type);
                            setPeriodFormula(null);
                        } else if (sFilter.operator == SUP_EQUAL && sFilter.value) {
                            setStartDate(sFilter.value);
                        } else if (sFilter.operator == SUP_EQUAL && sFilter.formula) {
                            setStartDateFormula(sFilter.formula);
                        } else if (sFilter.operator == INF_EQUAL && sFilter.value) {
                            setEndDate(sFilter.value);
                        } else if (sFilter.operator == INF_EQUAL && sFilter.formula) {
                            setEndDateFormula(sFilter.formula);
                        }
                    }
                });
            }
        }
    }, [filter]);

    // The default may change while mounted (dashboards: the aggregation selects
    // live next to the filter editor); follow it as long as no period is set.
    useEffect(() => {
        if (!period && !periodFormula) {
            setPeriodType(defaultPeriodType);
        }
    }, [defaultPeriodType, period, periodFormula]);

    // A new filter row applies the period the editor proposes (the current
    // one, of the selected type) right away: no need to change the value first.
    const autoApplied = useRef(false);
    useEffect(() => {
        if (disabled || autoApplied.current || periodType === undefined) {
            return;
        }
        if (period || periodFormula || filter?.groupFilter?.filters?.length) {
            return;
        }
        autoApplied.current = true;
        validatePeriod(currentPeriodOf(periodType));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [periodType]);

    if (!fieldKey || fieldPeriodType === undefined) {
        return null;
    }
    const shortcutPeriodType: PeriodType = matchablePeriodType(field, aggregation, fieldByKeys) ?? fieldPeriodType;

    const onPeriodTypeChange = (periodTypeasStr: string) => {
        const newPeriodType = parseInt(periodTypeasStr) as PeriodType;
        let newPeriod = period;
        if (newPeriod) {
            newPeriod = newPeriod.toPeriodType(newPeriodType);
        }
        setPeriod(newPeriod);
        setPeriodType(newPeriodType);
        if (newPeriod) {
            validatePeriod(newPeriod);
        }
    }

    function validatePeriod(period: Period | undefined) {

        if (!selectedPathKey) {
            return;
        }

        const newFieldFilters: Filter[] = [];
        if (period) {
            newFieldFilters.push({
                singleFilter: {
                    pathKey: selectedPathKey,
                    operator: IN,
                    value: period,
                    valueKey: null,
                    formula: null
                },
                groupFilter: null
            });
        }

        let fieldFilter: Filter | null = {
            singleFilter: null,
            groupFilter: {
                filters: newFieldFilters,
                combinator: OR
            }
        }

        validateFilter(fieldFilter);
        setPeriod(period);
        setPeriodType(period?.type);
        setPeriodFormula(null);
        setStartDate(null);
        setStartDateFormula(null);
        setEndDate(null);
        setEndDateFormula(null);
    }

    function validatePeriodFormula(formula: string) {
        if (!selectedPathKey) return;

        const newFieldFilters: Filter[] = [];
        if (formula && formula.length > 0) {
            newFieldFilters.push({
                singleFilter: {
                    pathKey: selectedPathKey,
                    operator: IN,
                    value: null,
                    valueKey: null,
                    formula: formula
                },
                groupFilter: null
            });
        }

        validateFilter({
            singleFilter: null,
            groupFilter: {
                filters: newFieldFilters,
                combinator: OR
            }
        });

        setPeriodFormula(formula);
        setPeriod(undefined);
        setPeriodType(undefined);
        setStartDate(null);
        setStartDateFormula(null);
        setEndDate(null);
        setEndDateFormula(null);
        setIsFormulaOpen(false);
    }

    function getLimitDatesFilter(startDate: string | null, startDateFormula: string | null, endDate: string | null, endDateFormula: string | null): Filter | null {
        if (!selectedPathKey) return null;

        const newFieldFilters: Filter[] = [];
        if (startDate && startDate.length > 0) {
            newFieldFilters.push({
                singleFilter: { pathKey: selectedPathKey, operator: SUP_EQUAL, value: startDate, valueKey: null, formula: null },
                groupFilter: null
            });
        } else if (startDateFormula && startDateFormula.length > 0) {
            newFieldFilters.push({
                singleFilter: { pathKey: selectedPathKey, operator: SUP_EQUAL, value: null, valueKey: null, formula: startDateFormula },
                groupFilter: null
            });
        }

        if (endDate && endDate.length > 0) {
            newFieldFilters.push({
                singleFilter: { pathKey: selectedPathKey, operator: INF_EQUAL, value: endDate, valueKey: null, formula: null },
                groupFilter: null
            });
        } else if (endDateFormula && endDateFormula.length > 0) {
            newFieldFilters.push({
                singleFilter: { pathKey: selectedPathKey, operator: INF_EQUAL, value: null, valueKey: null, formula: endDateFormula },
                groupFilter: null
            });
        }

        return { singleFilter: null, groupFilter: { filters: newFieldFilters, combinator: AND } };
    }

    function onLimitDatesChange(newStartDate: string | null, newStartDateFormula: string | null, newEndDate: string | null, newEndDateFormula: string | null) {
        const fieldFilter = getLimitDatesFilter(newStartDate, newStartDateFormula, newEndDate, newEndDateFormula);
        if (fieldFilter) {
            setStartDate(newStartDate);
            setStartDateFormula(newStartDateFormula);
            setEndDate(newEndDate);
            setEndDateFormula(newEndDateFormula);
            setPeriodFormula(null);
            setPeriod(undefined);
            setPeriodType(undefined);
            validateFilter(fieldFilter);
        }
        setIsFormulaOpen(false);
    }

    function onDeleteFormula() {
        setPeriodFormula(null);
        setIsFormulaOpen(false);

        if (!selectedPathKey) return;

        validateFilter({
            singleFilter: null,
            groupFilter: {
                filters: [],
                combinator: OR
            }
        });
    }

    const onFormulaButtonOpen = () => {
        if (!disabled) {
            setIsFormulaOpen(true);
        }
    }

    function onDeleteLimitDates() {
        setStartDate(null);
        setStartDateFormula(null);
        setEndDate(null);
        setEndDateFormula(null);
        if (!selectedPathKey) return;
        validateFilter({ singleFilter: null, groupFilter: { filters: [], combinator: OR } });
    }

    const isLimitDatesMode = startDate !== null || startDateFormula !== null || endDate !== null || endDateFormula !== null;

    if (isLimitDatesMode) {
        const startLabel = startDateFormula ?? startDate ?? "-∞";
        const endLabel = endDateFormula ?? endDate ?? "+∞";
        const popoverContent = (
            <PeriodFormulaEditor
                fieldKey={fieldKey}
                fieldPeriodType={shortcutPeriodType}
                periodFormula={periodFormula}
                onPeriodFormulaChange={validatePeriodFormula}
                onDeleteClicked={onDeleteLimitDates}
                selectedWorkspaceKey={selectedWorkspace?._key}
                startDate={startDate}
                startDateFormula={startDateFormula}
                endDate={endDate}
                endDateFormula={endDateFormula}
                onLimitDatesChange={onLimitDatesChange}
                dateFormat={field?.format}
            />
        );
        return (
            <div className="flex flex-row justify-start items-start gap-2 px-1 min-h-9 w-90">
                <div className="flex flex-col justify-center items-start gap-0.5 grow overflow-hidden">
                    <div className="flex flex-row items-center gap-1 w-full">
                        <span className={TW_PeriodFilterValueEditor_From + " w-8 min-w-8"}>{t("filter.period.from.label")}</span>
                        <Button className={TW_DateFilterValueEditor_PeriodFormulaButton} onClick={onFormulaButtonOpen}>
                            {startLabel}
                        </Button>
                    </div>
                    <div className="flex flex-row items-center gap-1 w-full">
                        <span className={TW_PeriodFilterValueEditor_To + " w-8 min-w-8"}>{t("filter.period.to.label")}</span>
                        <Button className={TW_DateFilterValueEditor_PeriodFormulaButton} onClick={onFormulaButtonOpen}>
                            {endLabel}
                        </Button>
                    </div>
                </div>
                <Popover open={isFormulaOpen} onOpenChange={setIsFormulaOpen}>
                    <PopoverTrigger onClick={onFormulaButtonOpen}>
                        <FunctionSVG className={TW_DateFilterValueEditor_FunctionButton + (disabled ? " cursor-not-allowed opacity-60" : " cursor-pointer")} />
                    </PopoverTrigger>
                    <PopoverContent className="w-96 p-1">{popoverContent}</PopoverContent>
                </Popover>
            </div>
        );
    }

    if (periodFormula) {
        const shortcutLabel = shortcuts.find(s => s.formula === periodFormula)?.label;
        const formulaLabel = shortcutLabel ?? periodFormula;

        return (
            <div className="flex flex-row justify-start items-center gap-2 px-1 h-9 w-90">
                <div className="flex flex-row justify-center items-center min-w-8 w-8">
                    <span className="text-sm text-gray-600">{t("filter.period.in.label")}</span>
                </div>
                <div className="flex flex-row justify-center items-center gap-2 grow overflow-hidden">
                    <Button className={TW_DateFilterValueEditor_PeriodFormulaButton} onClick={onFormulaButtonOpen}>
                        {formulaLabel}
                    </Button>
                </div>
                <Popover open={isFormulaOpen} onOpenChange={setIsFormulaOpen}>
                    <PopoverTrigger onClick={onFormulaButtonOpen}>
                        <FunctionSVG className={TW_DateFilterValueEditor_FunctionButton + (disabled ? " cursor-not-allowed opacity-60" : " cursor-pointer")} />
                    </PopoverTrigger>
                    <PopoverContent className="w-96 p-1">
                        <PeriodFormulaEditor
                            fieldKey={fieldKey}
                            fieldPeriodType={shortcutPeriodType}
                            periodFormula={periodFormula}
                            onPeriodFormulaChange={validatePeriodFormula}
                            onDeleteClicked={onDeleteFormula}
                            selectedWorkspaceKey={selectedWorkspace?._key}
                            startDate={startDate}
                            startDateFormula={startDateFormula}
                            endDate={endDate}
                            endDateFormula={endDateFormula}
                            onLimitDatesChange={onLimitDatesChange}
                            dateFormat={field?.format}
                        />
                    </PopoverContent>
                </Popover>
            </div>
        );
    }

    // Same wrapper (and width) as the other value editors so that the rows stay
    // aligned; it never shrinks (dashboards), the field selector does. The
    // period editor gets a fixed width so that the year and day inputs
    // (w-full) do not collapse in the flex row.
    return (
        <div className={TW_FilterEditorValue_Wrapper + " shrink-0"}>
            <Select onValueChange={onPeriodTypeChange} value={periodType?.toString() ?? ""} disabled={disabled}>
                <SelectTrigger className={(periodType !== undefined ? TW_PeriodTypeSelect_selected : TW_PeriodTypeSelect)}>
                    <SelectValue placeholder={t("period.type.placeholder")} />
                </SelectTrigger>
                <SelectContent>
                    {PeriodTypeValues.map((periodType) => {
                        // Finer than the displayed periods (e.g. a month while seeing quarters)
                        const disabled = !isFilterPeriodTypeAllowed(periodType, defaultPeriodType);
                        return (
                            <SelectItem className={TW_PeriodTypeSelectItem} key={periodType} value={periodType.toString()} disabled={disabled}>{getPeriodTypeLabel(periodType, t)}</SelectItem>
                        )
                    })}
                </SelectContent>
            </Select>
            {periodType != undefined &&
                <div className="flex flex-row items-center w-48 min-w-48">
                    <PeriodEditor initialPeriod={period} periodType={periodType} updatePeriod={validatePeriod} disabled={disabled} />
                </div>
            }
            <Popover open={isFormulaOpen} onOpenChange={setIsFormulaOpen}>
                <PopoverTrigger asChild>
                    <FunctionSVG className={TW_DateFilterValueEditor_FunctionButton + (disabled ? " cursor-not-allowed opacity-60" : " cursor-pointer")} onClick={onFormulaButtonOpen} />
                </PopoverTrigger>
                <PopoverContent className="w-96 p-1">
                    <PeriodFormulaEditor
                        fieldKey={fieldKey}
                        fieldPeriodType={shortcutPeriodType}
                        periodFormula={periodFormula}
                        onPeriodFormulaChange={validatePeriodFormula}
                        onDeleteClicked={onDeleteFormula}
                        selectedWorkspaceKey={selectedWorkspace?._key}
                        startDate={startDate}
                        startDateFormula={startDateFormula}
                        endDate={endDate}
                        endDateFormula={endDateFormula}
                        onLimitDatesChange={onLimitDatesChange}
                        dateFormat={field?.format}
                    />
                </PopoverContent>
            </Popover>
        </div>
    );

}

type PeriodFormulaEditorProps = {
    fieldKey: { objectKey: string; fieldKey: string };
    fieldPeriodType: PeriodType;
    periodFormula: string | null;
    onPeriodFormulaChange: (formula: string) => void;
    onDeleteClicked: () => void;
    selectedWorkspaceKey: string | undefined;
    startDate: string | null;
    startDateFormula: string | null;
    endDate: string | null;
    endDateFormula: string | null;
    onLimitDatesChange: (startDate: string | null, startDateFormula: string | null, endDate: string | null, endDateFormula: string | null) => void;
    dateFormat: Map<string, any> | undefined | null;
}

function PeriodFormulaEditor({ fieldKey, fieldPeriodType, periodFormula, onPeriodFormulaChange, onDeleteClicked, selectedWorkspaceKey, startDate, startDateFormula, endDate, endDateFormula, onLimitDatesChange, dateFormat }: PeriodFormulaEditorProps) {
    const { t } = useTranslation();
    const shortcuts = getPeriodShortcuts(t);
    const lang = useUserStore((state) => state.lang);

    const [shortcutsOpen, setShortcutsOpen] = useState(true);
    const [formulaOpen, setFormulaOpen] = useState(false);
    const [limitDatesFormulaOpen, setLimitDatesFormulaOpen] = useState(false);
    const [formulaValue, setFormulaValue] = useState<string>(periodFormula ?? "");
    const [errorMessage, setErrorMessage] = useState<string>('');

    const [startDateFormulaOpen, setStartDateFormulaOpen] = useState(false);
    const [endDateFormulaOpen, setEndDateFormulaOpen] = useState(false);
    const [startDateFormulaValue, setStartDateFormulaValue] = useState<string | null>(startDateFormula);
    const [endDateFormulaValue, setEndDateFormulaValue] = useState<string | null>(endDateFormula);
    const [editStartDate, setEditStartDate] = useState(false);
    const [editEndDate, setEditEndDate] = useState(false);
    const [startDateValue, setStartDateValue] = useState<string | null>(startDate);
    const [endDateValue, setEndDateValue] = useState<string | null>(endDate);

    const wsColor = useModelStore((state) => state.wsColor);
    const selectedWorkspace = useModelStore((state) => state.selectedWorkspace);
    const trashHover = useIconHover("#00000088", wsColor);

    useEffect(() => {
        if (periodFormula && !shortcuts.find(s => s.formula === periodFormula)) {
            openPeriodFormula();
        } else if (startDateFormula !== null || endDateFormula !== null) {
            openLimitDatesFormula();
        }
    }, []);

    const openShortcuts = () => {
        setShortcutsOpen(true);
        setFormulaOpen(false);
        setLimitDatesFormulaOpen(false);
    }

    const openPeriodFormula = () => {
        setFormulaOpen(true);
        setShortcutsOpen(false);
        setLimitDatesFormulaOpen(false);
    }

    const openLimitDatesFormula = () => {
        setLimitDatesFormulaOpen(true);
        setFormulaOpen(false);
        setShortcutsOpen(false);
    }

    function shortcutButton(label: string, shortcutFormula: string, disabled: boolean) {
        const buttonStyle = {
            borderColor: (shortcutFormula === periodFormula) ? wsColor : GRAY_300_COLOR,
            opacity: disabled ? 0.5 : 1,
            cursor: disabled ? "not-allowed" : "pointer",
        }
        return (
            <Button key={label} className={TW_AdvancedPeriodEditorShortcutButton} onClick={() => onPeriodFormulaChange(shortcutFormula)} style={buttonStyle} disabled={disabled}>
                {label}
            </Button>
        )
    }

    const onValidate = () => {
        checkFormula(selectedWorkspaceKey, fieldKey?.objectKey, undefined, formulaValue, () => {
            onPeriodFormulaChange(formulaValue);
        }, (errorI18n: string, _: any, errorParams: any) => {
            setErrorMessage(translate(t, errorI18n, errorParams));
        });
    }

    function dateToText(date: string | null, defaultText: string): string {
        if (!date) return defaultText;
        return displayDate(date, dateFormat, AggregationType.NO_AGGR, t);
    }

    const startDateToText = () => dateToText(startDateValue, "-∞");
    const endDateToText = () => dateToText(endDateValue, "+∞");

    const renderStartDateEditingContent = (
        onBlur: () => void,
        onEnterKeyPress: (event: React.KeyboardEvent) => void,
        onTextChange: (event: React.ChangeEvent<HTMLInputElement>) => void) => (
        <div className={TW_DateFilterValueEditor_Wrapper + " min-w-48 grow"}>
            <input className={TW_DateFilterValueEditor_editing} type="date" lang={lang}
                value={startDateValue ?? ""} onBlur={onBlur} onKeyDown={onEnterKeyPress} onChange={onTextChange} />
        </div>
    )

    const renderEndDateEditingContent = (
        onBlur: () => void,
        onEnterKeyPress: (event: React.KeyboardEvent) => void,
        onTextChange: (event: React.ChangeEvent<HTMLInputElement>) => void) => (
        <div className={TW_DateFilterValueEditor_Wrapper + " min-w-48 grow"}>
            <input className={TW_DateFilterValueEditor_editing} type="date" lang={lang}
                value={endDateValue ?? ""} onBlur={onBlur} onKeyDown={onEnterKeyPress} onChange={onTextChange} />
        </div>
    )

    function onEnterKeyPressEditing(event: React.KeyboardEvent, isStartDate: boolean) {
        if (AUTHORIZED_KEYS.includes(event.key)) return;
        let newValue = isStartDate ? startDateValue : endDateValue;
        if (event.key === "Enter") {
            event.preventDefault();
            event.stopPropagation();
            validateDateInput(newValue, isStartDate);
            isStartDate ? setEditStartDate(false) : setEditEndDate(false);
        } else if (event.key === "Tab") {
            event.preventDefault();
            event.stopPropagation();
            validateDateInput(newValue, isStartDate);
            if (isStartDate) { setEditStartDate(false); setEditEndDate(true); }
            else { setEditEndDate(false); setEditStartDate(true); }
        } else if (event.key === "Escape") {
            event.preventDefault();
            isStartDate ? setEditStartDate(false) : setEditEndDate(false);
        } else {
            newValue = newValue ? newValue + event.key : event.key;
        }
    }

    function validateDateInput(date: string | null | undefined, isStartDate: boolean) {
        if (isStartDate) setStartDateValue(date ?? null);
        else setEndDateValue(date ?? null);
    }

    function onTextChange(event: React.ChangeEvent<HTMLInputElement>, isStartDate: boolean) {
        if (isStartDate) setStartDateValue(event.target.value);
        else setEndDateValue(event.target.value);
    }

    function onLimitDatesFormulaValidate() {
        checkFormula(selectedWorkspace?._key, fieldKey?.objectKey, undefined, startDateFormulaValue || "", () => {
            checkFormula(selectedWorkspace?._key, fieldKey?.objectKey, undefined, endDateFormulaValue || "", () => {
                onLimitDatesChange(startDateValue, startDateFormulaValue, endDateValue, endDateFormulaValue);
            }, (errorI18n, _, errorParams) => {
                setErrorMessage(translate(t, errorI18n, errorParams));
            });
        }, (errorI18n, _, errorParams) => {
            setErrorMessage(translate(t, errorI18n, errorParams));
        });
    }

    function onStartDateFomulaChanged(formula: string | null) {
        setStartDateFormulaValue(formula);
        if (formula) setStartDateValue(null);
    }

    function onEndDateFomulaChanged(formula: string | null) {
        setEndDateFormulaValue(formula);
        if (formula) setEndDateValue(null);
    }

    return (
        <div className={TW_AdvancedPeriodEditorContainer}>
            <div className="flex flex-row justify-end items-center w-full relative">
                <TooltipProvider>
                    <Tooltip delayDuration={100}>
                        <TooltipTrigger asChild>
                            <FunctionStruckSVG className={TW_AdvancedPeriodEditorTrashIcon} onClick={onDeleteClicked} {...trashHover} />
                        </TooltipTrigger>
                        <TooltipContent>{t("filter.period.formula.delete.tooltip")}</TooltipContent>
                    </Tooltip>
                </TooltipProvider>
            </div>
            <div className={TW_AdvancedPeriodEditorSectionContainer}>
                <div className={TW_AdvancedPeriodEditorSectionHeader} onClick={openShortcuts}>
                    <Label className={TW_AdvancedPeriodEditorSectionLabel}>{t("filter.period.shortcuts.label")}</Label>
                    {shortcutsOpen ? <ChevronDown className={TW_AdvancedPeriodEditorSectionIcon} /> : <ChevronRight className={TW_AdvancedPeriodEditorSectionIcon} />}
                </div>
                <div className={TW_AdvancedPeriodEditorShortcutButtonsContainer + (shortcutsOpen ? " block" : " hidden")}>
                    {shortcuts.map((shortcut) => shortcutButton(shortcut.label, shortcut.formula, shortcut.periodType < fieldPeriodType))}
                </div>
            </div>
            <div className={TW_AdvancedPeriodEditorSectionContainer}>
                <div className={TW_AdvancedPeriodEditorSectionHeader} onClick={openPeriodFormula}>
                    <Label className={TW_AdvancedPeriodEditorSectionLabel}>{t("filter.period.formula.label")}</Label>
                    {formulaOpen ? <ChevronDown className={TW_AdvancedPeriodEditorSectionIcon} /> : <ChevronRight className={TW_AdvancedPeriodEditorSectionIcon} />}
                </div>
                <div className={TW_AdvancedPeriodEditorPeriodFormulaContainer + (formulaOpen ? " block" : " hidden")}>
                    <FormulaEditor formula={formulaValue || undefined} objectKey={fieldKey?.objectKey} onFormulaChange={setFormulaValue} fieldSelectorEnabled={false} />
                    <Button className={TW_AdvancedPeriodEditorPeriodFormulaButtonValidate} onClick={onValidate}>{t("filter.period.formula.validate.button.label")}</Button>
                    {errorMessage && <span className="text-red-500 text-sm">{errorMessage}</span>}
                </div>
            </div>
            <div className={TW_AdvancedPeriodEditorSectionContainer}>
                <div className={TW_AdvancedPeriodEditorSectionHeader} onClick={openLimitDatesFormula}>
                    <Label className={TW_AdvancedPeriodEditorSectionLabel}>{t("filter.period.limit.dates.formula.label")}</Label>
                    {limitDatesFormulaOpen ? <ChevronDown className={TW_AdvancedPeriodEditorSectionIcon} /> : <ChevronRight className={TW_AdvancedPeriodEditorSectionIcon} />}
                </div>
                <div className={TW_AdvancedPeriodEditorPeriodFormulaContainer + (limitDatesFormulaOpen ? " block" : " hidden")}>
                    <div className={TW_AdvancedPeriodEditorDateFormulasContainer}>
                        <div className={TW_AdvancedPeriodEditorDateFormulaItem + " relative"}>
                            <span className={TW_PeriodFilterValueEditor_From + " w-8 min-w-8"}>{t("filter.period.from.label")}</span>
                            {editStartDate && renderStartDateEditingContent(
                                () => { validateDateInput(startDateValue, true); setEditStartDate(false); },
                                (e) => onEnterKeyPressEditing(e, true),
                                (e) => onTextChange(e, true)
                            )}
                            {!editStartDate && !startDateFormulaValue && (
                                <div className={TW_DateFilterValueEditor_Wrapper + " min-w-48 grow"} onClick={() => setEditStartDate(true)}>
                                    <div className={TW_DateFilterValueEditor + " cursor-pointer"}>{startDateToText()}</div>
                                </div>
                            )}
                            {!editStartDate && startDateFormulaValue && (
                                <Button className={TW_DateFilterValueEditor_DateFormulaButton + " grow"} onClick={() => setStartDateFormulaOpen(!startDateFormulaOpen)}>
                                    {startDateFormulaValue}
                                </Button>
                            )}
                            <FunctionSVG color={startDateFormulaOpen ? GRAY_800_COLOR : GRAY_600_COLOR}
                                className={startDateFormulaOpen ? TW_AdvancedPeriodEditorFormulaTogglePressed : TW_AdvancedPeriodEditorFormulaToggle}
                                onClick={() => setStartDateFormulaOpen(!startDateFormulaOpen)} />
                            <FunctionStruckSVG width="24" height="24" color={GRAY_600_COLOR} className="p-1 min-w-8 min-h-8 w-8 h-8 cursor-pointer rounded-md hover:bg-gray-100 color-gray-600 border-1 border-gray-200 bg-transparent"
                                onClick={() => setStartDateFormulaValue(null)} />
                        </div>
                        {startDateFormulaOpen && <FormulaEditor formula={startDateFormulaValue || undefined} objectKey={fieldKey?.objectKey} onFormulaChange={onStartDateFomulaChanged} fieldSelectorEnabled={false} />}
                        <div className={TW_AdvancedPeriodEditorDateFormulaItem + " relative"}>
                            <span className={TW_PeriodFilterValueEditor_To + " w-8 min-w-8"}>{t("filter.period.to.label")}</span>
                            {editEndDate && renderEndDateEditingContent(
                                () => { validateDateInput(endDateValue, false); setEditEndDate(false); },
                                (e) => onEnterKeyPressEditing(e, false),
                                (e) => onTextChange(e, false)
                            )}
                            {!editEndDate && !endDateFormulaValue && (
                                <div className={TW_DateFilterValueEditor_Wrapper + " min-w-48 grow"} onClick={() => setEditEndDate(true)}>
                                    <div className={TW_DateFilterValueEditor + " cursor-pointer"}>{endDateToText()}</div>
                                </div>
                            )}
                            {!editEndDate && endDateFormulaValue && (
                                <Button className={TW_DateFilterValueEditor_DateFormulaButton + " grow"} onClick={() => setEndDateFormulaOpen(!endDateFormulaOpen)}>
                                    {endDateFormulaValue}
                                </Button>
                            )}
                            <FunctionSVG color={endDateFormulaOpen ? GRAY_800_COLOR : GRAY_600_COLOR}
                                className={endDateFormulaOpen ? TW_AdvancedPeriodEditorFormulaTogglePressed : TW_AdvancedPeriodEditorFormulaToggle}
                                onClick={() => setEndDateFormulaOpen(!endDateFormulaOpen)} />
                            <FunctionStruckSVG width="24" height="24" color={GRAY_600_COLOR} className="p-1 min-w-8 min-h-8 w-8 h-8 cursor-pointer rounded-md hover:bg-gray-100 color-gray-600 border-1 border-gray-200 bg-transparent"
                                onClick={() => setEndDateFormulaValue(null)} />
                        </div>
                        {endDateFormulaOpen && <FormulaEditor formula={endDateFormulaValue || undefined} objectKey={fieldKey?.objectKey} onFormulaChange={onEndDateFomulaChanged} fieldSelectorEnabled={false} />}
                        <div className={TW_AdvancedPeriodEditorPeriodFormulaContainer}>
                            <Button className={TW_AdvancedPeriodEditorPeriodFormulaButtonValidate} onClick={onLimitDatesFormulaValidate}>{t("filter.period.formula.validate.button.label")}</Button>
                            {errorMessage && <span className="text-red-500 text-sm">{errorMessage}</span>}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    )
}
